<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import type { QuasarLanguage } from 'quasar';
import { useLocalStorage } from '@vueuse/core';
import BCard from 'shared/components/base/BCard/BCard.vue';
import { mdiCheck } from '@quasar/extras/mdi-v7';

interface LocaleOption {
  label: string;
  value: string;
  flag: string;
}

const $q = useQuasar();
const { locale } = useI18n();
const localeStorage = useLocalStorage('locale', '');

// Import Quasar language packs dynamically
const quasarLangModules = import.meta.glob<{ default: QuasarLanguage }>(
  '/node_modules/quasar/lang/(en-US|he|uk).js',
);

// Define available languages
const langOptions: LocaleOption[] = [
  { label: 'English', value: 'en-US', flag: '🇺🇸' },
  { label: 'עברית', value: 'he-IL', flag: '🇮🇱' },
  { label: 'Українська', value: 'uk-UA', flag: '🇺🇦' },
];

const lang = ref(locale.value);

// Watch for language changes and update both i18n and Quasar
watch(lang, async (val) => {
  // Update vue-i18n locale
  locale.value = val;
  localeStorage.value = val;

  // Map i18n locale to Quasar locale
  const quasarLocaleMap: Record<string, string> = {
    'en-US': 'en-US',
    'he-IL': 'he',
    'uk-UA': 'uk',
  };

  const quasarLocale = quasarLocaleMap[val] || 'en-US';
  const modulePath = `/node_modules/quasar/lang/${quasarLocale}.js`;

  // Dynamically load and set Quasar language pack
  try {
    const langModule = quasarLangModules[modulePath];
    if (langModule) {
      const loadedLang = await langModule();
      $q.lang.set(loadedLang.default);
    }
  } catch (error) {
    console.error('Failed to load Quasar language pack:', error);
  }

  // Reload page to apply changes throughout the app
  window.location.reload();
});
</script>

<template>
  <BCard
    size="sm"
    class="rounded-borders-xl"
    :class="$q.dark.isActive ? 'shadow-card-dark' : 'shadow-card bg-white'"
    :dark="$q.dark.isActive"
  >
    <q-card-section>
      <div class="text-h6">{{ $t('SYSTEM_LANGUAGE') }}</div>
      <div class="text-caption text-muted">
        {{ $t('SELECT_SYSTEM_LANGUAGE') }}
      </div>
    </q-card-section>

    <q-separator />

    <q-card-section>
      <q-select
        v-model="lang"
        :options="langOptions"
        option-value="value"
        option-label="label"
        emit-value
        map-options
        outlined
        :label="$t('SELECT_LANGUAGE')"
        :dark="$q.dark.isActive"
      >
        <template #option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section avatar>
              <div class="text-h5">{{ scope.opt.flag }}</div>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ scope.opt.label }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-icon
                v-if="scope.opt.value === lang"
                :name="mdiCheck"
                color="primary"
              />
            </q-item-section>
          </q-item>
        </template>

        <template #selected>
          <div class="row items-center q-gutter-sm">
            <div class="text-h6">
              {{
                langOptions.find((opt: LocaleOption) => opt.value === lang)
                  ?.flag
              }}
            </div>
            <div>
              {{
                langOptions.find((opt: LocaleOption) => opt.value === lang)
                  ?.label
              }}
            </div>
          </div>
        </template>
      </q-select>
    </q-card-section>
  </BCard>
</template>
